import React from 'react'
// libs
import { FieldArray, reduxForm } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput } from 'components/FormElements/ReduxForm'
import renderIpAddresses from './fields/IpAddressList'
import ScopesField from './fields/ScopesField'
// context
import { ApiClientsPageContext } from 'containers/ApiClients/layers/API'
// constants
import { Forms } from 'constants/forms'
import { EXPIRATION_MAX_DAYS, EXPIRATION_MIN_DAYS } from 'constants/apiClient'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
import { IApiClientCreatedCredentials } from 'types/graphql'
// utils
import { handleError } from 'utils/errors/handlers'
import { validateScopes } from 'utils/apiClient'
import { maxValue, minValue, required } from 'utils/validators'
// views
import { Actions, Form } from './views'

const nameValidators = [required]
const scopeValidators = [validateScopes]
const expirationValidators = [required, minValue(EXPIRATION_MIN_DAYS), maxValue(EXPIRATION_MAX_DAYS)]

const cleanIpList = (list?: string[]): string[] => (list || []).map((ip) => ip.trim()).filter(Boolean)

interface Props {
  hideModal: () => void
  onCreated: (credentials: IApiClientCreatedCredentials) => void
}

const CreateForm: React.FC<InjectedFormProps<any, Props> & Props> = ({
  valid,
  hideModal,
  onCreated,
  submitting,
  handleSubmit
}) => {
  const { createApiClient } = React.useContext(ApiClientsPageContext)

  const handleFormSubmit = async (data: any) => {
    try {
      const credentials = await createApiClient({
        name: data.name,
        scopes: data.scopes || [],
        expiresInDays: Number(data.expiresInDays),
        allowedIpRanges: cleanIpList(data.allowedIpRanges)
      })

      onCreated(credentials)
    } catch (error) {
      handleError(error, 'ApiClientCreateModal.handleFormSubmit')
    }
  }

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <ReduxInput name='name' label='Name' validate={nameValidators} />
      <ReduxInput
        name='expiresInDays'
        label={`Expiration in days (${EXPIRATION_MIN_DAYS}–${EXPIRATION_MAX_DAYS})`}
        type='number'
        inputProps={{ min: EXPIRATION_MIN_DAYS, max: EXPIRATION_MAX_DAYS, step: 1 }}
        validate={expirationValidators}
      />
      <FieldArray name='allowedIpRanges' component={renderIpAddresses} />
      <ScopesField name='scopes' validate={scopeValidators} />

      <Actions>
        <Button type='submit' layout={BUTTON_LAYOUTS.PURPLE} disabled={!valid || submitting} isLoading={submitting}>
          Create
        </Button>
        <Button type='button' layout={BUTTON_LAYOUTS.WHITE} onClick={hideModal}>
          Cancel
        </Button>
      </Actions>
    </Form>
  )
}

export default reduxForm<any, Props>({
  form: Forms.API_CLIENT_CREATE_FORM,
  initialValues: { expiresInDays: 90, scopes: [], allowedIpRanges: [] }
})(CreateForm)
